import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomInput from "../../components/textField/scmInput/CustomInput";
import axios from 'axios';
import { faFileCirclePlus } from '@fortawesome/free-solid-svg-icons';
import {
    getListDirectory,
    postRequestForLatestDirectory,
} from "../../services/FileFromMachineAPI";

import {
    getUploadedUrl,
    uploadFileToMachine,
} from "../../services/FileToMachineAPI";

import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import "./FileToMachine.css";

import TreeView from "../../components/Tree Structure/TreeView";
import { toast } from "react-toastify";

import {
    faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatToBrowserTime } from "../../utils/timeFormat";
import RequestUpdateButton from "../../components/directoryRequest/RequestUpdateButton";

const FileToMachine = ({ location }) => {
    const { t } = useTranslation();
    const ctx = useOutletContext?.() || {};

    const qs = new URLSearchParams(location?.search ?? "");
    const model = ctx?.model ?? qs.get("model") ?? "";
    const serialNumber = ctx?.serialNumber ?? qs.get("serialNumber") ?? "";

    const [treeItems, setTreeItems] = useState([]);
    const [selectedNodePath, setSelectedNodePath] = useState("");
    const [filePath, setFilePath] = useState("");
    const [lastUpdatedDate, setLastUpdatedDate] = useState("");
    const [data, setData] = useState([]);
    const [treeLoading, setTreeLoading] = useState(false);

    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(Date.now());
    const [downloadLoading, setDownloadLoading] = useState(false);

    const hasCheckedInitialDirectory = useRef(false);

    const isRequestUpdateInProgress = () => {
        const until = Number(
            localStorage.getItem(`fileToMachine_requestUpdate_${model}_${serialNumber}`)
        );
        return Boolean(until && until > Date.now());
    };

    const handleTreeClick = async (node) => {
        if (!node?.path) return;

        setFilePath(node.name);
        setSelectedNodePath(node.path);

        try {
            const params = {
                model,
                serial: serialNumber,
                directory: node.path,
                custom_directories: true,
            };

            const table_data = await getListDirectory(params);
            const files = table_data?.directory_listing?.[0]?.File ?? [];

            const mapped = files.map((f, idx) => ({
                id: idx + 1,
                name: f.name,
                size: f.size,
                date: f?.lastModified ? formatToBrowserTime(f.lastModified) : "",
                path: `${node.path}/${f.name}`,
            }));

            setData(mapped);
        } catch {
            toast.error("Failed to load directory contents");
        } finally {
        }
    };

    const loadData = async () => {
        if (!model || !serialNumber) return;

        setTreeLoading(true);

        try {
            const raw = await getListDirectory({
                model,
                serial: serialNumber,
                custom_directories: true,
            });

            if (!raw || raw.length < 1) {
                if (
                    !hasCheckedInitialDirectory.current &&
                    !isRequestUpdateInProgress()
                ) {
                    toast.error(
                        "No directory listing found. Please click 'Request Update' to fetch the latest data."
                    );
                }
                hasCheckedInitialDirectory.current = true;
                return;
            }

            setLastUpdatedDate(
                raw?.last_updated
                    ? dayjs(raw.last_updated).format("YYYY-MM-DD HH:mm")
                    : ""
            );

            const treeViewFormatted =
                raw.directory_listing?.map((item) => ({
                    name: item.displayName,
                    path: item.path,
                    children: [],
                })) || [];

            hasCheckedInitialDirectory.current = true;
            setTreeItems(treeViewFormatted);

            if (treeViewFormatted.length > 0) {
                handleTreeClick(treeViewFormatted[0]);
            }
        } catch {
            toast.error("Failed to load directory listing");
        } finally {
            setTreeLoading(false);
        }
    };


    const handleFileChange = (e) => {
        const f = e.target.files?.[0] || null;
        setFile(f);
    };

    const handleRemoveFile = () => {
        setFile(null);
        setFilePath("");
        setFileInputKey(Date.now());
        if (fileInputRef.current) fileInputRef.current.value = "";

    };

    // ---- UPLOAD ----
    const handleUpload = async () => {
        if (!file || !selectedNodePath) return;

        const toastId = toast.loading("Uploading file... 0%", { progress: 0 });
        const contentType = file.type || "application/octet-stream";

        try {
            setDownloadLoading(true);

            const response = await getUploadedUrl({
                model,
                serial: serialNumber,
                file_name: file.name,
                contentType,
            });

            const bareAxios = axios.create();
            await bareAxios.put(response.uploadUrl, file, {
                headers: { "Content-Type": contentType },
                onUploadProgress: (e) => {
                    const progress = Math.round((e.loaded / e.total) * 100);
                    toast.update(toastId, {
                        render: `Uploading... ${progress}%`,
                        progress: progress / 100,
                    });
                },
            });

            await uploadFileToMachine({
                job_type: "UPLOAD",
                s3_object_keys: [response.uploadS3Key],
                destination: selectedNodePath,
                target_type: "device",
                target_name: `${model}-${serialNumber}`,
                clear_target: false,
            });

            toast.success(t("Request to Transfer file sent successfully"));
        } catch (err) {
            console.error(err);
            toast.error("Failed to upload file");
        } finally {
            toast.dismiss(toastId);
            setDownloadLoading(false);
        }
    };

    useEffect(() => {
        hasCheckedInitialDirectory.current = false;
        loadData();

        setFile(null);
        setFileInputKey(Date.now());
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [model, serialNumber]);

    return (
        <div className="fb-page-wrapper">
            {/* -------- HEADER -------- */}
            <div className="fb-header">
                <div className="fs-header-update">
                    <span className="fs-label">Last Updated - {lastUpdatedDate}</span>
                    <RequestUpdateButton
                        scope="fileToMachine"
                        model={model}
                        serial={serialNumber}
                        requestFn={() =>
                            postRequestForLatestDirectory({
                                model,
                                serial: serialNumber,
                                custom_directories: true,
                            })
                        }
                        onSuccess={loadData}
                    />
                </div>

                <div className="fs-header-buttons">

                    <button className="btn btn-primary" onClick={loadData}>
                        <FontAwesomeIcon icon={faRefresh} className="me-1" />
                        Refresh
                    </button>
                </div>
            </div>

            {/* -------- MAIN LAYOUT -------- */}
            <div className="fb-header-content">
                {/* ---- Sidebar ---- */}
                <aside className="fb-sidebar">
                    <div className="fb-treeview-container">
                        <TreeView
                            data={treeItems}
                            onNodeClick={handleTreeClick}
                            selectedNodePath={selectedNodePath}
                            loading={treeLoading}
                            isFlat={true}
                        />
                    </div>
                </aside>

                {/* ---- Main Content ---- */}
                <main className="scrollable-main">

                    <CustomInput
                        label={t('Upload Path')}
                        value={filePath}
                        readOnly
                        placeholder={t('Selected upload path')}
                    />

                    <label className="form-label">Select File</label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="form-control"
                        onChange={handleFileChange}
                        key={fileInputKey}
                        disabled={selectedNodePath.length === 0 || downloadLoading}
                    />
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />

                    {/* Selected File Preview */}
                    <div className="fb-content">
                        <div>
                            {file && (
                                <div>
                                    <div className="mt-2 small text-muted  align-items-center">
                                        <span title={file.name}>
                                            {file.name} ({file.size.toLocaleString()} bytes)
                                        </span>

                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary ms-auto margin-left-5px"
                                            onClick={handleRemoveFile}
                                            disabled={downloadLoading}
                                        >
                                            {t("Remove")}
                                        </button>
                                    </div>

                                    <div className="fb-actions margin-top-5px">
                                        <button className="btn btn-primary" onClick={handleUpload} title={t('Download to Selected Folder')} disabled={downloadLoading} >
                                            <FontAwesomeIcon icon={faFileCirclePlus} />   Download to Selected Folder
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default FileToMachine;
